import { Delete } from "@mui/icons-material";
import {
    Button,
    FormControl,
    FormHelperText,
    Grid,
    InputAdornment,
    InputLabel,
    List,
    ListItem,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { useAddRow } from "app/frontend/components/eligibility/accordions/project_size/hooks/useAddRow";
import ProjectSizeAccordionAddPlugButton from "app/frontend/components/eligibility/accordions/project_size/ProjectSizeAccordionAddPlugButton";
import { IProjectSizeFormData } from "app/frontend/components/eligibility/accordions/project_size/types";
import FormNumberInput from "app/frontend/components/forms/incentives/form_elements/FormNumberInput";
import QuestionTooltip from "app/frontend/components/QuestionTooltip";
import { EProjectTypes } from "app/frontend/constants/enums";
import { useFormContext } from "app/frontend/contexts/useFormContext";
import { useIsMHD } from "app/frontend/hooks/useIsMHD";
import { useCallback, useState } from "react";

const PLUG_TYPES = [EProjectTypes.L2, EProjectTypes.DCFC];

interface IProps {
    index: number;
    isLastItem: boolean;
    disabled: boolean;
}

const ProjectSizeAccordionRow: React.FC<IProps> = ({ index, isLastItem, disabled }) => {
    const { data, getData, setData, unsetData, errors, setError } = useFormContext<IProjectSizeFormData>();

    const addRow = useAddRow();

    const isMHD = useIsMHD();

    const removeRow = useCallback(() => {
        if (index === 0) return;
        unsetData(`plugs[${index}]`);
    }, [index, unsetData]);

    const [countFocused, _setCountFocused] = useState<boolean>(false);
    const [amountKwFocused, _setAmountKwFocused] = useState<boolean>(false);
    const setCountFocused = () => _setCountFocused(true);
    const unsetCountFocused = () => _setCountFocused(false);
    const setAmountKwFocused = () => _setAmountKwFocused(true);
    const unsetsetAmountKwFocused = () => _setAmountKwFocused(false);

    return (
        <>
            <Grid size={{ xs: 12, sm: isMHD ? 4 : 6 }} sx={{ mx: isMHD ? undefined : "auto" }}>
                <FormNumberInput
                    as={TextField}
                    workingProjectAmount={getData(`plugs[${index}].count`)}
                    setWorkingProjectAmount={(newAmount: number) => {
                        setData(`plugs[${index}].count`, newAmount);
                    }}
                    input={{
                        name: `count_${index}`,
                        id: `count_${index}`,
                        label: (
                            <Typography component={"label"}>
                                Plug Count{isMHD ? " (2 - 1000) *" : ""}
                                {countFocused && (
                                    <QuestionTooltip
                                        input={{
                                            tooltip: (
                                                <Typography component={"p"} variant="body2">
                                                    Enter the number of plugs (not the number of chargers). Some
                                                    chargers have more than one plug.
                                                </Typography>
                                            ),
                                        }}
                                    />
                                )}
                            </Typography>
                        ),
                        variant: "standard",
                        min: isMHD ? 2 : 1,
                        max: 1000,
                        disabled,
                        onFocus: setCountFocused,
                        onBlur: unsetCountFocused,
                        slotProps: {
                            input: {
                                endAdornment: (
                                    <InputAdornment
                                        sx={{
                                            display: {
                                                md: "none",
                                                xl: "inline",
                                            },
                                        }}
                                        position="end"
                                    >
                                        plugs
                                    </InputAdornment>
                                ),
                                required: !data.isProjectSizeUnknown,
                                disabled,
                            },
                        },
                    }}
                />
            </Grid>
            {isMHD && (
                <Grid size={{ xs: 12, sm: isMHD ? 4 : 6 }}>
                    <FormControl fullWidth error={!!errors.plug_type}>
                        {/* https://www.figma.com/design/Omzyb3Rm9htxnqFipDGlgu/PowerReady-w-Bellawatt?node-id=375-269&m=dev */}
                        <InputLabel id={"mhd_plug_type-label"}>Type *</InputLabel>
                        <Select
                            id={`mhd_plug_type_${index}`}
                            variant="standard"
                            name={`plug_type_${index}`}
                            required={!data.isProjectSizeUnknown}
                            label={"Type"}
                            labelId={"mhd_plug_type-label"}
                            slotProps={{
                                input: {
                                    id: `mhd_plug_type_${index}`,
                                    "aria-labelledby": "mhd_plug_type-label",
                                },
                            }}
                            value={getData(`plugs[${index}].plug_type`)}
                            defaultValue={""}
                            onChange={(e) => setData(`plugs[${index}].plug_type`, e.target.value)}
                            onBlur={() => setError(`plugs[${index}].plug_type`, "")}
                            disabled={disabled}
                        >
                            {PLUG_TYPES.map((plug) => (
                                <MenuItem key={plug} value={plug} sx={{ textWrap: "wrap" }}>
                                    {plug === "L2" ? "Level 2" : plug}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.plug_type && <FormHelperText sx={{ mx: 0 }}>{errors.plug_type}</FormHelperText>}
                    </FormControl>
                </Grid>
            )}
            <Grid size={{ xs: 12, sm: isMHD ? 4 : 6 }} sx={{ mx: isMHD ? undefined : "auto" }}>
                <FormNumberInput
                    as={TextField}
                    workingProjectAmount={getData(`plugs[${index}].amount_kw`)}
                    setWorkingProjectAmount={(newAmount: number) => {
                        setData(`plugs[${index}].amount_kw`, newAmount);
                    }}
                    input={{
                        name: `amount_kw_${index}`,
                        id: `amount_kw_${index}`,
                        max: 1000,
                        label: (
                            <Typography component={"label"}>
                                Capacity *
                                {amountKwFocused && (
                                    <QuestionTooltip
                                        input={{
                                            tooltip: (
                                                <Typography
                                                    component={"div"}
                                                    variant="body2"
                                                    sx={{ lineHeight: "20px" }}
                                                >
                                                    The following are guides for plug capacity based on vehicle type:
                                                    <List>
                                                        <ListItem sx={{ ml: 0 }}>
                                                            • 20kW: Utility Truck, School Bus, Walk-In Van, Delivery
                                                            Truck
                                                        </ListItem>
                                                        <ListItem sx={{ ml: 0 }}>
                                                            • 50kW: Transit Bus, Refuse Truck
                                                        </ListItem>
                                                        <ListItem sx={{ ml: 0 }}>• 100kW: Drayage</ListItem>
                                                    </List>
                                                </Typography>
                                            ),
                                        }}
                                    />
                                )}
                            </Typography>
                        ),
                        onFocus: setAmountKwFocused,
                        onBlur: unsetsetAmountKwFocused,
                        variant: "standard",
                        disabled,
                        slotProps: {
                            input: {
                                endAdornment: (
                                    <InputAdornment
                                        sx={{
                                            display: {
                                                md: "none",
                                                xl: "inline",
                                            },
                                        }}
                                        position="end"
                                    >
                                        kW
                                    </InputAdornment>
                                ),
                                required: !data.isProjectSizeUnknown,
                                disabled,
                            },
                        },
                    }}
                />
            </Grid>
            {isMHD && !disabled && (
                <Grid size={{ xs: 12 }} sx={{ alignItems: "center" }}>
                    {isLastItem && (
                        <ProjectSizeAccordionAddPlugButton
                            addRow={addRow}
                            sx={{ display: { xs: "initial", md: "none" }, pl: 0 }}
                        />
                    )}
                    {index !== 0 && (
                        <Button
                            variant="outlined"
                            onClick={removeRow}
                            sx={{ height: "100%", float: { xs: "right" }, py: 2 }}
                        >
                            <Delete />
                        </Button>
                    )}
                </Grid>
            )}
        </>
    );
};

export default ProjectSizeAccordionRow;
